import {queryOptions} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {BookmarkIcon, PlusCircleIcon} from '@primer/octicons-react'

import type {QuickSearchSuggestion} from '../../quick-search-types'
import type {ParsedQuery} from '../suggestion-utils'

export type SavedSearch = {
  name: string
  query: string
}

const SAVED_SEARCH_PATH = '/search/custom_scopes'

export const manageSavedSearchesSuggestion: QuickSearchSuggestion = {
  id: 'manage-saved-searches',
  displayName: 'Manage saved searches',
  icon: PlusCircleIcon,
  ariaLabel: 'Manage saved searches, Action',
  action: 'manage-saved-searches',
  group: 'saved',
}

export function savedSearchesQueryOptions() {
  return queryOptions({
    queryKey: [SAVED_SEARCH_PATH],
    queryFn: async () => {
      const response = await reactFetchJSON(SAVED_SEARCH_PATH)
      if (!response.ok) throw new Error('Failed to fetch saved search suggestions')

      const responseJson = (await response.json()) as SavedSearch[]
      return responseJson
    },
    staleTime: 60 * 1000,
    placeholderData: prev => prev,
  })
}

function mapSavedSearchSuggestion(s: SavedSearch): QuickSearchSuggestion {
  // Saved searches are inserted as a `saved:` qualifier token (matching the
  // legacy search behaviour) rather than navigating away. Names containing
  // whitespace are quoted; single-word names are inserted unquoted.
  const needsQuotes = /\s/.test(s.name)
  const token = needsQuotes ? `saved:"${s.name}"` : `saved:${s.name}`
  return {
    id: `saved-search-${s.name}`,
    displayName: token,
    icon: BookmarkIcon,
    insertValue: `${token} `,
    ariaLabel: `${token}, Autocomplete`,
    action: 'autocomplete',
    group: 'saved',
  }
}

export function savedSearchSuggestionsQueryOptions(query: ParsedQuery) {
  // Saved-search suggestions are shown in three cases:
  //   • no active segment  → show every saved search (initial state)
  //   • plain-text segment → filter by the typed value (name substring match)
  //   • `saved:` segment   → filter by whatever the user has typed after
  //                          `saved:`, so `saved:pr` narrows to names
  //                          containing "pr"
  // For any other qualifier (`org:`, `repo:`, `language:`, ...) we return
  // nothing so saved searches don't distract from scope-specific autocomplete.
  const {activeSegment} = query
  const nameFilter: string | undefined = !activeSegment
    ? ''
    : activeSegment.type === 'saved' || activeSegment.type === 'plain'
      ? activeSegment.value
      : undefined

  return queryOptions({
    ...savedSearchesQueryOptions(),
    select: (responseJson: SavedSearch[]): QuickSearchSuggestion[] => {
      if (nameFilter === undefined) return []
      return responseJson.filter(s => s.name?.includes(nameFilter)).map(mapSavedSearchSuggestion)
    },
  })
}
